/**
 * mediaUploader — Iter 133 (Apr 2026) · Iter 140 hotfix (Apr 2026).
 *
 * Iter 133 introduced direct-to-bucket presigned PUTs targeting an
 * R2/S3 endpoint. In the preview pod the operator-provided AWS env
 * vars are unconfigured placeholders (`LINEAGEBETAACCESSKEYID`,
 * `lineage-beta.r2.cloudflarestorage.com` — DNS does NOT resolve),
 * so the browser's XHR PUT failed with a generic `Error de red al
 * subir a la nube` exactly as the founder reported on the story
 * upload screen.
 *
 * Iter 140 hotfix: route `uploadToCloud()` through the proven
 * `/api/uploads/media` proxy that ALREADY ships bytes to the
 * Emergent Object Storage successfully (image uploads, avatars, and
 * post media all use it). This restores story / challenge uploads
 * without depending on an external R2 bucket that isn't provisioned
 * in the preview environment.
 *
 * The public surface (`uploadToCloud`, `requestPresignedUrl`,
 * `putToBucket`) is preserved so callers — `StoryUploadDialog.js`
 * and `SubmitChallengeMediaDialog.js` — keep working untouched.
 *
 *   uploadToCloud(file, folder, opts) → { url, kind, filename,
 *                                          size_bytes, content_type,
 *                                          key, _detectedKind }
 *
 * Error model: throws `UploadError` (re-used from `lib/uploadsApi.js`)
 * so existing toast / retry logic keeps working untouched.
 */

import {
  classifyFile,
  validateFile,
  UploadError,
  uploadsApi,
} from './uploadsApi';
import { tr } from '@/contexts/LanguageContext';

const BACKEND_HOST = process.env.REACT_APP_BACKEND_URL || '';
const TOKEN_KEY    = 'lineage_token';

/** Build the `Authorization` header from localStorage (only when present). */
const _authHeader = () => {
  try {
    const t = localStorage.getItem(TOKEN_KEY);
    return t ? { Authorization: `Bearer ${t}` } : {};
  } catch {
    return {};
  }
};

/** Step 1 — kept for backward-compat. The current path no longer
 *  consults this endpoint (the Emergent proxy handles everything
 *  in one round-trip via `uploadsApi.uploadMedia`), but the function
 *  is still exported for callers that wired their own R2 path. */
export async function requestPresignedUrl(file, folder = 'challenges') {
  if (!file) throw new UploadError(tr("Selecciona un archivo."));
  validateFile(file);
  const res = await fetch(`${BACKEND_HOST}/api/media/presigned-url`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ..._authHeader() },
    credentials: 'include',
    body: JSON.stringify({
      file_name: file.name || 'file',
      file_type: file.type || 'application/octet-stream',
      folder,
    }),
  });
  let payload = null;
  try { payload = await res.json(); } catch { /* non-JSON 5xx */ }
  if (!res.ok) {
    const detail =
      (payload && (payload.detail?.[0]?.msg || payload.detail)) ||
      tr("No se pudo preparar la subida. Inténtalo de nuevo.");
    throw new UploadError(typeof detail === 'string' ? detail : tr("Error al firmar la URL."), {
      status: res.status,
    });
  }
  return payload;
}

/** Step 2 — kept for backward-compat. Plain PUT to a presigned URL.
 *  Modern callers should use `uploadToCloud` (single round-trip via
 *  the Emergent Object Storage proxy). */
export function putToBucket(uploadUrl, file, { onProgress, signal, headers = {} } = {}) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', uploadUrl, true);
    if (file?.type) xhr.setRequestHeader('Content-Type', file.type);
    Object.entries(headers || {}).forEach(([k, v]) => {
      if (k.toLowerCase() === 'content-type') return;
      xhr.setRequestHeader(k, v);
    });
    if (xhr.upload && typeof onProgress === 'function') {
      xhr.upload.onprogress = (e) => {
        if (!e.lengthComputable) return;
        onProgress(Math.round((e.loaded / e.total) * 95));
      };
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        if (typeof onProgress === 'function') onProgress(100);
        resolve({ status: xhr.status });
      } else {
        reject(new UploadError(
          tr("La subida falló. Inténtalo de nuevo."),
          { status: xhr.status },
        ));
      }
    };
    xhr.onerror = () => reject(new UploadError(tr("Error de red al subir a la nube.")));
    xhr.onabort = () => reject(new UploadError(tr("Subida cancelada.")));
    if (signal) {
      if (signal.aborted) { xhr.abort(); return; }
      signal.addEventListener('abort', () => xhr.abort());
    }
    xhr.send(file);
  });
}

/** Convenience wrapper — chains step 1 + step 2 with the same shape
 *  the legacy `uploadsApi.uploadMedia` returns, so existing dialogs
 *  (StoryUploadDialog, SubmitChallengeMediaDialog) keep working.
 *
 *  Iter 140 hotfix: implementation now delegates to
 *  `uploadsApi.uploadMedia` (server-side proxy to Emergent Object
 *  Storage) because the R2 presigned PUT path is unreachable in the
 *  preview pod. The behaviour from the caller's POV is identical:
 *  progress callback, abort signal, and same return shape.
 */
export async function uploadToCloud(file, folder = 'challenges', opts = {}) {
  if (!file) throw new UploadError(tr("Selecciona un archivo."));
  validateFile(file);
  const kind = classifyFile(file);
  // The server-side proxy stamps the folder via the URL prefix once
  // it lands in Emergent Object Storage; on the client we don't have
  // to pass it explicitly. The legacy code preserved `folder` for
  // future direct-to-bucket revival, so we keep accepting it without
  // forwarding to the proxy endpoint.
  const result = await uploadsApi.uploadMedia(file, {
    onProgress: opts.onProgress,
    signal:     opts.signal,
  });
  return {
    url:           result.url,
    kind:          result.kind || kind,
    filename:      result.filename ?? file.name,
    size_bytes:    result.size_bytes ?? file.size,
    content_type:  result.content_type ?? file.type,
    // The proxy doesn't expose the storage key directly; expose the
    // URL as `key` so consumers that previously read `key` still get
    // a stable identifier.
    key:           result.key || result.url,
    _detectedKind: kind,
    _folder:       folder,
  };
}

export const mediaUploader = {
  requestPresignedUrl,
  putToBucket,
  uploadToCloud,
};

export default mediaUploader;
